import { ClientFactory } from "@/main/common/ClientFactory";
import { RequestCoalescer } from "@/main/common/RequestCoalescer";

/**
 * The viewer's safety relationship with one other account, as `UserButtons` needs it.
 *
 * This wrapper exists so the read can be coalesced: the same person's card mounts more than once on a
 * single page (inline discovery row + desktop rail), and each mount used to issue its own request. See
 * `designs/01-now/2026-07-24-home-discovery-duplicate-mount-design.md`.
 */
export type ViewerUserSafetyState = {
    blockedByViewer: boolean;
    viewerBlockedByUser: boolean;
    mutedByViewer: boolean;
};

const safetyStateCoalescer = new RequestCoalescer<ViewerUserSafetyState | null>();

export const UserSafetyApi = {
    getSafetyState(username: string): Promise<ViewerUserSafetyState | null> {
        return safetyStateCoalescer.run(username, async () => {
            // The raw error is intentionally left untouched on the failure path: callers distinguish an
            // expected 4xx from a real 5xx by reading `err.response.status`.
            const response = await ClientFactory.getAjaxUserRestControllerApi().getSafetyState(username);
            const state = response?.data;
            if (!state) {
                return null;
            }

            return {
                blockedByViewer: Boolean(state.blockedByViewer),
                viewerBlockedByUser: Boolean(state.viewerBlockedByUser),
                mutedByViewer: Boolean(state.mutedByViewer),
            };
        });
    },

    /** Call after a block / unblock / mute / unmute so a remount inside the reuse window is not stale. */
    invalidate(username: string) {
        safetyStateCoalescer.invalidate(username);
    },

    resetForTests() {
        safetyStateCoalescer.clear();
    },
};
